import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle, Package } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PreviewBatchDialog({ open, onClose, onCreated, preselectStatementIds, preselectLabel }) {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState('');

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        include_recovery: true,
        include_zero_net: true,
      };
      // Phase 27.30.4 — scope the batch to a specific set of statements
      // when the dialog was opened from a Carrier Import deep-link.
      if (preselectStatementIds?.length) {
        payload.statement_ids = preselectStatementIds;
      }
      const { data } = await accountingAPI.previewBatch(payload);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    } finally { setLoading(false); }
  }, [preselectStatementIds]);

  useEffect(() => {
    if (open) {
      setPreview(null);
      setNotes('');
      loadPreview();
    }
  }, [open, loadPreview]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = {
        include_recovery: true,
        include_zero_net: true,
        notes: notes.trim() || undefined,
      };
      if (preselectStatementIds?.length) {
        payload.statement_ids = preselectStatementIds;
      }
      const { data } = await accountingAPI.createBatch(payload);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create batch');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="preview-batch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" /> New Payment Batch — Preview
          </DialogTitle>
          <DialogDescription>
            Review the recipients + statements that will be included. Blocked recipients are excluded automatically.
            Negative recovery balances are applied FIFO up to the available amount.
          </DialogDescription>
        </DialogHeader>

        {preselectLabel && (
          <div className="mx-6 -mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex items-center gap-2">
            <Package className="h-3.5 w-3.5" />
            <span>Scoped to <b>{preselectLabel}</b> — only these statements will be included.</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-2">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…
            </div>
          ) : !preview ? null : (
            <div className="space-y-4">
              {/* Summary strip */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <SummaryCell label="Recipients" value={preview.recipient_count} />
                <SummaryCell label="Positive" value={money(preview.total_positive)} tone="emerald" />
                <SummaryCell label="Offset Applied" value={preview.total_offset > 0 ? `-${money(preview.total_offset)}` : money(0)} tone="orange" />
                <SummaryCell label="Credits" value={preview.total_manual_credit > 0 ? `+${money(preview.total_manual_credit)}` : money(0)} tone="violet" />
                <SummaryCell label="Net Payable" value={money(preview.total_net_payable)} tone="slate" bold />
              </div>

              {/* Blocked warning */}
              {preview.excluded_blocked_count > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex gap-3" data-testid="blocked-warning">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-medium text-amber-900">
                      {preview.excluded_blocked_count} recipient(s) excluded — {money(preview.excluded_blocked_payable)} outstanding
                    </div>
                    <ul className="mt-1 text-amber-800 text-xs space-y-0.5">
                      {preview.excluded_blocked_recipients.slice(0, 5).map((r) => (
                        <li key={r.recipient_id}>
                          <b>{r.recipient_name}</b> — {r.reason_labels.join(', ')} ({money(r.outstanding_payable)})
                        </li>
                      ))}
                      {preview.excluded_blocked_recipients.length > 5 && (
                        <li className="italic">+ {preview.excluded_blocked_recipients.length - 5} more…</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Lines table */}
              {preview.line_count === 0 ? (
                <div className="p-12 flex flex-col items-center text-slate-500 border border-dashed rounded-md">
                  <Package className="h-10 w-10 text-slate-300 mb-3" />
                  <div className="text-sm">No eligible recipients found. Every payable statement is either already batched or blocked.</div>
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-slate-600">
                        <th className="text-left px-3 py-2 font-medium">Recipient</th>
                        <th className="text-left px-3 py-2 font-medium">Method</th>
                        <th className="text-right px-3 py-2 font-medium">Positive</th>
                        <th className="text-right px-3 py-2 font-medium">Offset</th>
                        <th className="text-right px-3 py-2 font-medium">Credit</th>
                        <th className="text-right px-3 py-2 font-medium">Net</th>
                        <th className="text-right px-3 py-2 font-medium">Statements</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.lines.map((ln) => (
                        <tr key={ln.batch_line_id} className="border-b border-slate-100 last:border-0" data-testid={`preview-line-${ln.recipient_id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-slate-800">{ln.recipient_name}</div>
                            <div className="text-xs text-slate-500 uppercase">{ln.recipient_type}</div>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline">{ln.payment_method || '—'}</Badge>
                          </td>
                          <td className="px-3 py-2 text-right text-emerald-700">{money(ln.positive_total)}</td>
                          <td className="px-3 py-2 text-right text-orange-600">
                            {ln.negative_offset_applied > 0 ? `-${money(ln.negative_offset_applied)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-violet-600" data-testid={`preview-credit-${ln.recipient_id}`}>
                            {ln.manual_credit_applied > 0 ? `+${money(ln.manual_credit_applied)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {money(ln.net_payable)}
                            {ln.line_type === 'zero_net' && (
                              <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">Zero-net (fully applied)</div>
                            )}
                            {ln.line_type === 'manual_credit' && (
                              <div className="text-[10px] uppercase tracking-wide text-violet-500 mt-0.5">Standalone credit</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-600">{ln.statement_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-1.5">
                <Label htmlFor="batch-notes">Notes (optional)</Label>
                <Textarea
                  id="batch-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for this batch or reviewer instructions…"
                  rows={2}
                  data-testid="batch-notes"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={creating} data-testid="cancel-preview-btn">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!preview || creating || preview.line_count === 0}
            data-testid="create-batch-btn"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create Draft Batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCell({ label, value, tone = 'slate', bold = false }) {
  const toneMap = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    orange:  'text-orange-700 bg-orange-50 border-orange-200',
    violet:  'text-violet-700 bg-violet-50 border-violet-200',
    slate:   'text-slate-800 bg-slate-50 border-slate-200',
  };
  return (
    <div className={`rounded-md border p-2.5 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className={bold ? 'text-lg font-semibold' : 'text-base font-medium'}>{value}</div>
    </div>
  );
}
